import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import { useI18n } from "@/i18n/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1571753753463-636443ad284c?auto=format&fit=crop&w=2400&q=80";

export default function Home() {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/vehicles", { params: { featured: true } })
      .then((r) => setFeatured((r.data || []).slice(0, 3)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative h-[100svh] min-h-[600px] w-full overflow-hidden bg-[#0B0B0B]"
        data-testid="hero-section"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[75%_center] md:object-[80%_center] portrait:scale-[1.15]"
        />
        <div
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{
            height: "clamp(220px, 38vh, 480px)",
            background:
              "linear-gradient(to bottom, #0B0B0B 0%, #0B0B0B 22%, rgba(11,11,11,0.55) 60%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            height: "70%",
            background:
              "linear-gradient(to top, #0B0B0B 0%, rgba(11,11,11,0.85) 22%, transparent 100%)",
          }}
        />
        {/* Vertical left-to-right darkener: keeps the text column readable on mobile */}
        <div
          className="absolute inset-y-0 left-0 pointer-events-none md:hidden"
          style={{
            width: "100%",
            background:
              "linear-gradient(to right, rgba(11,11,11,0.92) 0%, rgba(11,11,11,0.6) 45%, rgba(11,11,11,0.15) 100%)",
          }}
        />
        <div
          className="absolute inset-y-0 left-0 pointer-events-none hidden md:block"
          style={{
            width: "65%",
            background:
              "linear-gradient(to right, rgba(11,11,11,0.85) 0%, rgba(11,11,11,0.35) 65%, transparent 100%)",
          }}
        />
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(0,0,0,0.35)_100%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 h-full flex flex-col justify-end">
          <div className="max-w-3xl fade-up">
            <h1
              data-testid="hero-headline"
              className="font-display text-[2.1rem] leading-[1.08] sm:text-5xl lg:text-6xl font-light tracking-tight text-white"
            >
              {t("hero.headline")}
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-5 md:mt-6 text-sm md:text-base text-zinc-300 leading-relaxed max-w-2xl"
            >
              {t("hero.subheadline")}
            </p>

            <p className="mt-4 text-[10px] sm:text-[11px] text-zinc-500 tracking-[0.34em] uppercase font-light">
              {t("hero.tagline")}
            </p>

            <div
              className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3"
              data-testid="hero-cta-group"
            >
              <Link
                to="/garage"
                className="btn-gold w-full sm:w-auto justify-center"
                data-testid="hero-view-garage"
              >
                {t("hero.cta.primary")} <ArrowRight size={14} />
              </Link>
              <Link
                to="/request"
                className="btn-ghost w-full sm:w-auto justify-center"
                data-testid="hero-request-car"
              >
                {t("hero.cta.secondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THE HOUSE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-5">
          <div className="section-label mb-6">{t("house.label")}</div>
          <h2 className="font-display text-[2rem] md:text-5xl lg:text-[3.25rem] font-light leading-[1.18] tracking-tight">
            {t("house.title.part1")}{" "}
            <span className="text-[#C6A15B] italic">{t("house.title.accent")}</span>.
          </h2>
        </div>
        <div className="md:col-span-7 md:pl-10 md:border-l border-white/5 space-y-5">
          <p className="text-zinc-300 text-base leading-relaxed">{t("house.p1")}</p>
          <p className="text-zinc-300 text-base leading-relaxed">{t("house.p2")}</p>
          <p className="text-zinc-400 text-xs sm:text-sm tracking-[0.22em] uppercase pt-2">
            {t("house.p3")}
          </p>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section
          className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24"
          data-testid="featured-section"
        >
          <div className="flex items-end justify-between mb-10 gap-6">
            <div>
              <div className="section-label mb-4">{t("featured.label")}</div>
              <h2 className="font-display text-3xl md:text-4xl font-light">
                {t("featured.title")}
              </h2>
            </div>
            <Link
              to="/garage"
              className="text-[11px] tracking-[0.3em] uppercase text-[#C6A15B] hover:text-[#D4B67A] flex items-center gap-2"
              data-testid="view-all-garage"
            >
              {t("featured.viewAll")} <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((v) => (
              <VehicleCard key={v.id} vehicle={v} />
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="border border-[#C6A15B]/25 p-8 md:p-16 bg-gradient-to-br from-[#0F0F0F] to-[#0B0B0B]">
            <div className="max-w-3xl">
              <div className="section-label mb-5">{t("cta.label")}</div>
              <h2 className="font-display text-3xl md:text-5xl font-light leading-tight mb-6">
                {t("cta.title.part1")}
                <br />
                <span className="italic text-[#C6A15B]">{t("cta.title.accent")}</span>
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-8 max-w-xl">{t("cta.body")}</p>
              <Link to="/request" className="btn-gold" data-testid="cta-request-car">
                {t("cta.button")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
